import type { ComponentProps } from "react"
import cn from "@/v2-ui/utils/utils.cn"
import Button from "@/v2-backoffice-client/components/button"
import { useTableContext } from "../table.context"
import { getColumnTitle } from "../table.utils"
import { isStatusFilterValue } from "../TableStatusFilter"
import { isDateFilterValue, formatDateFilterLabel } from "./dateFilter"
import { summarizeGroup } from "./tableAdvancedFilter.utils"

export type TableFilterListProps = ComponentProps<"div">

/**
 * Displays active column filters as dismissible badges, plus a summary chip
 * for the advanced (nested AND/OR/NOT) filter when one is active.
 * Only renders when there is at least one active filter.
 * Excludes filters managed by TableStatusFilter.
 */
function TableFilterList(props: TableFilterListProps) {
  const { className, ...divProps } = props
  const { table, advancedFilter, setAdvancedFilter } = useTableContext()

  const { columnFilters } = table.getState()
  const visibleFilters = columnFilters.filter(
    (f) => !isStatusFilterValue(f.value),
  )
  if (visibleFilters.length === 0 && !advancedFilter) return null

  const columnLabel = (columnId: string) => {
    const column = table.getColumn(columnId)
    return column ? getColumnTitle(column) : columnId
  }

  return (
    <div className={cn("ui-table-active-filters", className)} {...divProps}>
      <span className="ui-table-active-filters-label">Filtered by:</span>

      {advancedFilter && (
        <Button
          className="ui-table-active-filters-badge"
          variant="secondary"
          size="xs"
          iconSrc="cross-small"
          iconPosition="right"
          iconClassName="ui-table-icon-sm"
          title="Clear advanced filter"
          onClick={() => setAdvancedFilter(null)}
        >
          <span>{summarizeGroup(advancedFilter, columnLabel)}</span>
        </Button>
      )}

      {visibleFilters.map((f) => {
        const column = table.getColumn(f.id)
        if (!column) return null

        return (
          <Button
            key={f.id}
            className="ui-table-active-filters-badge"
            variant="secondary"
            size="xs"
            iconSrc="cross-small"
            iconPosition="right"
            iconClassName="ui-table-icon-sm"
            onClick={() => {
              column.setFilterValue(undefined)
            }}
          >
            <span>
              {getColumnTitle(column)}
              {isDateFilterValue(f.value)
                ? `: ${formatDateFilterLabel(f.value)}`
                : ""}
            </span>
          </Button>
        )
      })}

      <Button
        className="ui-table-active-filters-clear"
        variant="ghost"
        size="xs"
        onClick={() => {
          table.resetColumnFilters()
          setAdvancedFilter(null)
        }}
      >
        Clear all
      </Button>
    </div>
  )
}

export default TableFilterList
